import { Component, Mixins } from "vue-property-decorator";
import { IOrder } from "@pazion/pazion-catalog/src/models/order";
import {
  createOrder,
  getOrdersOnly,
  getOrderlinesIncludingOrderFields,
  getOrderStatuses,
  createBatchShipping, getOrderDetails,
} from "@pazion/pazion-catalog/src/services/catalog-service";
import { getTenantList, ITenant } from "@pazion/pazion-catalog/src/services/base-service";

import { executeCommand } from "@pazion/pazion-core/src/services/auth-service";
import { ITablePaginationOptions } from "@pazion/pazion-catalog/src/models/base";

import orderListingFilterMixin from "./orderListingFilterMixin";
import BaseMixin from "@pazion/pazion-catalog/src/mixins/base.mixin";
import PermissionService from "@pazion/pazion-core/src/services/permission-service";

@Component({})
export default class orderListingMixin extends Mixins(
  BaseMixin,
  PermissionService,
  orderListingFilterMixin
) {
  public showOrderCreator: boolean = false;
  public showOrderFilesModal: boolean = false;
  public selectedOrderObject =null;

  public searchData: {
    search: string;
    id?: string|number;
    searchInAttr: boolean;
    searchAdvInAttr?: boolean;
  } = { search: "", searchInAttr: false };

  //Paginations opt
  public defaultQueryPaginationsOpt = {
    pagination: 1,
    perPage: 100,
    page: 1,
  };
  public totalItems = 1;
  public paginationOptions: ITablePaginationOptions = { itemsPerPage: 100 };

  public tenantList: ITenant[] = [];

  get canShowTenant() {
    return this.hasFeatureFlag("tenantManagement") && this.hasProfileFlag('tenantManagement.show');
  }

  get tenantOptionsList() {
    return this.tenantList.map((tenant) => {
      return { title: tenant.name, value: tenant.id };
    });
  }

  getTetantName(account: any) {
    return (
      this.tenantList.find((tenant) => tenant.id === account.tenant)?.name || ""
    );
  }

  //
  row_classes() {
    return "v-row-group__subrow";
  }

  isOrderHasNotes(order: IOrder) {
    return order.notes;
  }

  async createNewBatchShipment({ orderList }: { orderList: IOrder[] }) {
    try {
      const orderIdsData = orderList.map((order) => {
        return { order: order.orderId || order.id };
      });
      await createBatchShipping({ orderIds: orderIdsData });
      this.$refs.toast.show({
        message: "New batch shipping successfully initiated",
        color: "green",
      });
      await this.fetchData();
    } catch (error) {
      this.showDefaultServerErrorToast();
    }
  }

  async createNewOrder() {
    if (!this.$orderAutoReference) this.showOrderCreator = true;
    else await this.newAutoOrderCreator();
  }

  newOrderCreated(orderId: number) {
    this.showOrderCreator = false;
    this.showOrderEditDialog = true;
    this.selectedOrder = orderId;
    this.fetchData();
  }

  async newAutoOrderCreator() {
    this.orderReference = `order-${Date.now()}`;
    this.loading = true;
    const rsp = await createOrder(this.orderReference);
    if (rsp.orderId && rsp.orderId > 0) {
      this.selectedOrderObject = await getOrderDetails(rsp.orderId);
      this.orderReference = "";
      this.showOrderEditDialog = true;
      this.selectedOrder = rsp.orderId;
      await this.fetchData();
    }
    this.loading = false;
  }

  // Show order files
  showOrderFiles(item: IOrder, fromGroup?: boolean) {
    this.selectedOrder = !fromGroup ? item.id : item.orderId;
    this.showOrderFilesModal = true;
  }

  isStateRetryable(state: any) {
    switch (state) {
      case "ERROR":
      case "ERROR_TO_SHIPMENT":
      case "ERROR_SHIPMENT":
      case "ERROR_INVOICE":
        return true;
    }
    return false;
  }

  async addQueJobCommand(orderId: number) {
    const orderCommand = {
      command: this.hasOrderCommand,
      material: orderId,
    };
    this.loading = true;
    const rsp = await executeCommand(orderCommand);
    if (!rsp || !rsp.success) {
      this.$refs.toast.show({
        message: "Failed to add the requested queue job",
        icon: "mdi-close-circle",
        color: "red",
      });
    } else {
      this.$refs.toast.show({
        message: "Succesfully added the requested queue job",
        color: "green",
      });
    }
    this.loading = false;
  }

  canDoProd(status: string) {
    return !["INVOICED", "PROCESSED", "SHIPPED", "DRAFT"].includes(status);
  }

  //NOTE: Create params from orderBy
  createOrderParams(): { [key: string]: "DESC" | "ASC" } | undefined {
    let orderBy: { [key: string]: "DESC" | "ASC" } | undefined = undefined;
    // @ts-ignore
    this.paginationOptions?.sortBy.length > 0 &&
      (orderBy = {
        [this.paginationOptions?.sortBy[0]]: this.paginationOptions.sortDesc[0]
          ? "DESC"
          : "ASC",
      });
    return orderBy || undefined;
  }

  /* NOTE: Fetch main Data */
  async fetchData(
    queryParams?: { [key: string]: any | { [key: string]: string } } | {},
    isAllRecords?: boolean,
    clearRef?: boolean,
    order?: { [key: string]: "ASC" | "DESC" },
    isCombo?: boolean,
    status?: { [key: string]: { [key: string]: string[] } }
  ) {
    let filter: { reference?: object,id?: number|string } = {};
    let idLookup=Number(this.$route.query && (this.$route.query.orderId));
    if(idLookup && idLookup > 0){
      Object.assign(queryParams, { id: idLookup });
      Object.assign(filter, { id: idLookup });
      this.$router.replace({ query: null }).catch((err) => {});
    }
    isCombo
      ? Object.assign(filter, { "o.isDeleted": 0, ...status })
      : Object.assign(filter, { isDeleted: 0, ...status });

    if (clearRef) {
      this.searchByReference = "";
      this.$router.replace({ query: null }).catch((err) => {});
    }

    let fullQeryParams = queryParams
      ? queryParams
      : this.defaultQueryPaginationsOpt;
    order && Object.assign(fullQeryParams, { orderBy: JSON.stringify(order) });

    const fullParams = {
      filter: JSON.stringify({ ...filter, ...status }),
      ...fullQeryParams,
      productionManagement: this.$productionManagement || false,
      orderDossierManagement: this.$orderDossierManagement || false,
    };
    try {
      this.loading = true;
      [this.orderStatuses, this.tenantList] = await Promise.all([
        getOrderStatuses({ activeStatuses: 1 }),
        getTenantList(),
      ]);

      let response = null;
      if(fullQeryParams.hasOwnProperty('id')){
        response = await getOrdersOnly({id: fullQeryParams.id});
      }
      if (isCombo) {
        response = await getOrderlinesIncludingOrderFields(fullParams);
      } else {
        response = await getOrdersOnly(fullParams);
      }

      if (!response) return;

      this.items = response;
      this.totalItems = response._pagination._total;
      Object.assign(this.paginationOptions, {
        page: response._pagination._page,
        itemsPerPage: isAllRecords
          ? this.paginationOptions.itemsPerPage
          : response._pagination._per_page,
      });

      this.availableStatusesItems = this.getAvailableStatuses();
      this.availableOrderTypes = this.getAvailableOrderTypes();
      this.availableOrderMarketplaces = this.getAvailableOrderMarketplaces();
      this.loading = false;
    } catch (error) {
      this.showDefaultServerErrorToast();
    }
  }

  updatePaginationFromListing(
    val: ITablePaginationOptions,
    oldVal: ITablePaginationOptions,
    isCombo: boolean,
    statusList?: string[]
  ) {
    if (
      val.itemsPerPage !== oldVal.itemsPerPage ||
      val.page !== oldVal.page ||
      val.sortBy.length > 0
    ) {
      let pagiantionParams;
      let orderBy;
      if (val.itemsPerPage > 0)
        pagiantionParams = {
          pagination: 1,
          perPage: val.itemsPerPage,
          page: val.page,
        };
      else {
        pagiantionParams = this.defaultQueryPaginationsOpt;
        pagiantionParams.perPage = this.paginationOptions.itemsPerPage;
      }
      orderBy = this.createOrderParams();
      //@todo HIERO NAKAMURA
      if (this.searchData.search.length > 0) {
        let filters = this.searchByReferenceFields(
          { ...this.searchData },
          isCombo
        );
        filters &&
          Object.assign(pagiantionParams, { filter: JSON.stringify(filters) });
      }

      let status;
      if (statusList?.length) {
        status = isCombo
          ? { "o.status": { in: statusList } }
          : { status: { in: statusList } };
      }
      this.fetchData(
        pagiantionParams,
        val.itemsPerPage === -1,
        false,
        orderBy,
        isCombo,
        status
      );
    }
  }

  get getOrdersArray() {
    return (this.items && this.items?._data) || [];
  }

  get hasOrderCommand() {
    return (
      this.$queJobAction &&
      this.$queJobAction.hasOwnProperty("orderListing") &&
      this.$queJobAction.orderListing
    );
  }
  get canCreateDelivery() {
    return this.hasProfileFlag('fulfilmentManagement.show') && this.$createDelivery != undefined && !!this.$createDelivery;
  }

  get canShowDeliveryDialog() {
    return this.hasProfileFlag('fulfilmentManagement.show') && this.$showDeliveryDialog != undefined && this.$showDeliveryDialog;
  }

  //Get table-data headers
  get getHeaders() {
    if (this.tableHeaders.length > 0) {
      return this.tableHeaders;
    }
    return this.canShowTenant
      ? this.headers.main
      : this.headers.main.filter((header) => header.text !== "Tenant");
  }

  get isCombinedOrderListing(): boolean {
    return this.localTypeStatus && this.localTypeStatus === "combo";
  }

  get canShowProductionManagement(){
    return this.hasProfileFlag('productionManagement.show');
  }

  //NOTE: Permissions
  get canEditOrder(): boolean {
    return this.hasProfileFlag("orderManagement.edit");
  }

  get canShowOrderDetails(): boolean {
    return this.hasProfileFlag("orderManagement.details");
  }
} //
